import React, { useCallback, useEffect, useState } from 'react'
import { path, pathOr, isEmpty, empty } from 'ramda'
import { Formik, FastField } from 'formik'
import * as yup from 'yup'
import { subscribe } from 'react-contextual'
import { useDispatch } from 'react-redux'
import { debounce } from 'lodash-es'
import {
  Modal,
  CurrencyField,
  TextField,
  FormField,
  Checkbox,
  CurrencyText,
  FormLoader,
  InfoBox,
  TextInput,
  Label,
} from '../../../../views/components'
import cx from 'classnames'
import { currencyUtils } from '../../../../utils'
import RefundProvider from '../../RefundProvider'
import styles from './RefundModal.module.scss'
import { walletApiEvents } from '../../../api/wallet'
import { $TSFixMe } from 'types/ts-migrate'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import { PAYMENT_REFERENCE_MAX, PAYMENT_REFERENCE_PATTERN } from 'utils/validation'

const validationSchema = yup.object().shape({
  amount: yup
    .number()
    .required('required')
    .test('amount', function (amount: $TSFixMe) {
      if (amount <= 0) {
        return this.createError({
          path: this.path,
          message: 'must be greater than 0',
        })
      }
      if (amount > this.parent.depositAccountBalance) {
        return this.createError({
          path: this.path,
          message: `can't be greater than deposit balance`,
        })
      }
      return true
    }),
  reference: yup
    .string()
    .required('required')
    .max(PAYMENT_REFERENCE_MAX, `must be ${PAYMENT_REFERENCE_MAX} characters or less`)
    .matches(
      PAYMENT_REFERENCE_PATTERN,
      'can only contain letters, numbers, and limited special characters: . - * , ( ) % + $ ; = @ ? : ~ / _',
    ),
  isConfirmed: yup.bool().oneOf([true], 'required'),
})

const ButtonContainer = ({ children }: any) => <div className={styles['button-container']}>{children}</div>

const RefundModal = ({
  handleClose,
  requestRefund,
  isOpen,
  hasApiErrors,
  generalErrors,
  // fieldErrors,
  isSubmitting,
  isLoading,
  tenantId,
  tenantAccount,
  tenantName,
  tenantDetails,
  depositAccountBalance,
  asset,
  partyBankingErrors,
}: $TSFixMe) => {
  const dispatch = useDispatch()

  const _handleClose = useCallback(
    resetForm => {
      resetForm()
      handleClose()
    },
    [handleClose],
  )

  useEffect(() => {
    isOpen && dispatch(walletApiEvents.balance_request({ partyId: tenantId }))
  }, [isOpen, dispatch, tenantId])

  const [wasSubmitting, setWasSubmitting] = useState(false)

  useEffect(() => {
    if (isSubmitting) {
      setWasSubmitting(true)
    }
  }, [isSubmitting])

  const debouncedClose = useCallback(
    debounce(
      () => {
        handleClose()
        setWasSubmitting(false)
      },
      1000,
      { leading: false },
    ),
    [handleClose, setWasSubmitting],
  )

  useEffect(() => {
    if (!isSubmitting && wasSubmitting && !hasApiErrors) {
      debouncedClose()
    }
  }, [isSubmitting, wasSubmitting, debouncedClose, hasApiErrors])

  const partyId = tenantDetails?.id
  const bankingErrors = !partyBankingErrors ? [] : partyBankingErrors?.errors.map(error => error.message)

  return (
    <Formik
      enableReinitialize
      validateOnMount={false}
      validationSchema={validationSchema}
      onSubmit={values => {
        const payload = { body: values, params: { partyId: values.partyId } }
        requestRefund(payload)
      }}
      initialValues={{
        amount: 0,
        reference: '',
        isConfirmed: false,
        partyId,
        account: tenantAccount?.accountId,
        asset: asset?.name,
        depositAccountBalance,
      }}
    >
      {({
        values,
        errors,
        touched,
        submitCount,
        isValid,
        handleSubmit,
        handleBlur,
        handleChange,
        setFieldValue,
        resetForm,
      }) => {
        const confirmationLabel = (
          <p>
            I confirm I'm refunding <strong>{tenantName}</strong> {currencyUtils.formatCurrency(values.amount || 0)} to
            the account above.
          </p>
        )

        const formLoaderState = isLoading
          ? 'loading'
          : isSubmitting
          ? 'submitting'
          : (submitCount > 0 && !isEmpty(errors)) ||
            hasApiErrors ||
            (partyBankingErrors !== undefined && !empty(partyBankingErrors))
          ? 'error'
          : undefined

        return (
          <Modal isOpen={isOpen} onClose={() => _handleClose(resetForm)}>
            <Modal.Title>
              <div>Refund deposit</div>
            </Modal.Title>
            {bankingErrors.length > 0 && (
              <InfoBox type="error">
                <p>There are issues with the bank details. Please fix by editing the contact and try again.</p>
                <p>If you have already updated the contacts banking details, please try again in 5 minutes.</p>
                <ul>
                  {bankingErrors.map((err, i) => (
                    <li key={`${err} ${i}`}>{err as string}</li>
                  ))}
                </ul>
              </InfoBox>
            )}
            <Modal.Body>
              <FormLoader
                buttonProps={{
                  children: 'Refund deposit',
                  disabled: !isValid || ['loading', 'error'].includes(formLoaderState as string),
                }}
                onSubmit={handleSubmit}
                state={formLoaderState}
                ButtonContainer={ButtonContainer}
                errorText={hasApiErrors ? pathOr('Oops, something went wrong.', [0], generalErrors) : 'Resolve errors'}
              >
                <FormField>
                  <FastField
                    name="amount"
                    render={({ field }: any) => (
                      <CurrencyField
                        {...field}
                        label="Refund amount"
                        error={path(['amount'], values) && path(['amount'], errors)}
                        onChange={({ target }: any) => setFieldValue('amount', target.value)}
                        onBlur={handleBlur}
                      />
                    )}
                    meta={{ depositAccountBalance }}
                  />
                </FormField>
                <div className={styles['balance-info']}>
                  <Label circular className={styles['balance-label']}>
                    Deposit balance: <CurrencyText>{depositAccountBalance}</CurrencyText>
                  </Label>
                </div>
                <FormField>
                  <FastField
                    name="reference"
                    render={({ field }: any) => (
                      <TextField
                        {...field}
                        inputComponent={
                          <TextInput
                            type={TextFieldTypes.text}
                            name="reference"
                            value={values.reference}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        }
                        error={path(['reference'], values) && path(['reference'], errors)}
                        label="Statement reference"
                        labelHint="This will appear on the recipient's bank statement"
                      />
                    )}
                  />
                </FormField>
                <FormField>
                  <p className={styles['bank-details-label']}>Payment to</p>
                  <p className={styles['bank-details']}>
                    <span>
                      <strong>Bank:</strong> {tenantDetails?.bank}
                    </span>
                    <span>
                      <strong>Account type:</strong> {tenantDetails?.accountType}
                    </span>
                    <span>
                      <strong>Account number:</strong> *****{tenantDetails?.accountNumber?.slice(-4)}
                    </span>
                    <span>
                      <strong>Account holder:</strong> {tenantDetails?.accountName}
                    </span>
                  </p>
                </FormField>
                <FormField>
                  <Checkbox
                    className={cx(styles.confirmation, {
                      [styles['has-error']]: submitCount > 0 && path(['isConfirmed'], errors),
                    })}
                    name="isConfirmed"
                    label={confirmationLabel}
                    checked={values.isConfirmed}
                    onChange={(e: any) => setFieldValue('isConfirmed', e.checked)}
                    onBlur={handleBlur}
                    error={submitCount > 0 && path(['isConfirmed'], errors)}
                  />
                </FormField>
              </FormLoader>
            </Modal.Body>
          </Modal>
        )
      }}
    </Formik>
  )
}

export default subscribe(
  [RefundProvider],
  ({
    refund: {
      handleClose,
      requestRefund,
      isOpen,
      hasApiErrors,
      generalErrors,
      // fieldErrors,
      isSubmitting,
      isLoading,
      tenantId,
      tenantAccount,
      tenantName,
      tenantDetails,
      depositAccountBalance,
      asset,
      partyBankingErrors,
    },
  }: $TSFixMe) => ({
    handleClose,
    requestRefund,
    isOpen,
    hasApiErrors,
    generalErrors,
    // fieldErrors,
    isSubmitting,
    isLoading,
    tenantId,
    tenantAccount,
    tenantName,
    tenantDetails,
    depositAccountBalance,
    asset,
    partyBankingErrors,
  }),
)(RefundModal)
